import { Component, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsFooterComponent } from '../mps-footer/mps-footer.component';
import { MpsHeaderComponent } from '../mps-header/mps-header.component';
import { MpsLoaderComponent } from '../mps-loader/mps-loader.component';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-root',
  templateUrl: './mps-app.component.html',
  styleUrl: './mps-app.component.scss',
  imports: [
    MpsLoaderComponent,
    MpsHeaderComponent,
    MpsFooterComponent,
    MpsNavigationComponent,
    TranslateModule,
    MatSidenavModule,
    RouterOutlet,
  ],
})
export class MpsAppComponent {
  private _matIconRegistry = inject(MatIconRegistry);
  private _domSanitizer = inject(DomSanitizer);
  private _translateService = inject(TranslateService);
  private _userService = inject(MpsUserService);
  private _user = signal(this._userService.getUserSubject().getValue());

  public constructor(private dateAdapter: DateAdapter<Date>) {
    this.dateAdapter.setLocale(environment.defaultLocale);
    this.registerSvgIcon();
    this.initTranslationService();
  }

  private initTranslationService() {
    this._translateService.setDefaultLang(environment.defaultLocale);
    this._translateService.addLangs(environment.allowedLanguage);

    // Set a default language if there's no language set.
    // Validate every candidate against the registered (allowed) languages so that
    // a stored or profile value outside allowedLanguage (e.g. 'fr' after EN-only
    // config) is silently skipped rather than applied without a UI to correct it.
    if (!this._translateService.currentLang) {
      const allowed = this._translateService.getLangs();
      const candidates = [this._user()?.lang, localStorage.getItem('lang')];
      const resolved = candidates.find((lang): lang is string => !!lang && allowed.includes(lang));
      this._translateService.use(resolved ?? environment.defaultLocale);
    }
  }

  private registerSvgIcon() {
    const icons = [...environment.mpsIcons, ...environment.appIcons];

    icons.forEach((icon) => {
      this._matIconRegistry.addSvgIcon(
        `total-${icon}`,
        this._domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/icons/${icon}.svg`)
      );
    });
  }
}
